import { createContext, useCallback, useEffect, useRef, useState } from 'react';
import type { User } from '../../../types';

export type LimitModalType = 'signin' | 'upgrade' | 'pdf' | null;

const AuthContext = createContext<{
  loading: boolean;
  user: User | null;
  setUser: (user: User | null) => void;
  refreshUser: () => Promise<void>;
  incrementUserSeconds: (seconds: number) => void;
  updateUserSpeed: (speed: number) => Promise<void>;
  updateAutoEmail: (autoEmail: boolean) => Promise<void>;
  updateUserCategories: (categoryIds: string[]) => Promise<void>;
  logout: () => void;
  deleteAccount: () => Promise<void>;
  hasActiveSubscription: () => boolean;
  checkListeningLimit: () => LimitModalType;
}>({
  loading: true,
  user: null,
  setUser: () => {},
  refreshUser: async () => {},
  incrementUserSeconds: () => {},
  updateUserSpeed: async () => {},
  updateAutoEmail: async () => {},
  updateUserCategories: async () => {},
  logout: () => {},
  deleteAccount: async () => {},
  hasActiveSubscription: () => false,
  checkListeningLimit: () => null,
});

const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);

  const fetchUser = useCallback(async () => {
    const response = await fetch('/api/me', {
      credentials: 'include',
    });
    const data = await response.json();
    setUser(data);
    return data;
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchUser().finally(() => setLoading(false));
  }, [fetchUser]);

  const refreshUser = useCallback(async () => {
    await fetchUser();
  }, [fetchUser]);

  const incrementUserSeconds = useCallback((seconds: number) => {
    setUser((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        seconds: prev.seconds + seconds,
        quotaSecondsTotal: prev.quotaSecondsTotal + seconds,
        listeningSecondsToday: prev.listeningSecondsToday + seconds,
        listeningSecondsThisMonth: prev.listeningSecondsThisMonth + seconds,
      };
    });
  }, []);

  const updateUserSpeed = useCallback(async (speed: number) => {
    try {
      const response = await fetch('/api/update-speed', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ speed }),
      });

      if (!response.ok) {
        console.error('Failed to update user speed');
        return;
      }

      setUser((prev) => {
        if (!prev) return prev;
        return { ...prev, speed };
      });
    } catch (error) {
      console.error('Error updating user speed:', error);
    }
  }, []);

  const updateAutoEmail = useCallback(async (autoEmail: boolean) => {
    try {
      const response = await fetch('/api/update-auto-email', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ autoEmail }),
      });

      if (!response.ok) {
        console.error('Failed to update email preference');
        return;
      }

      setUser((prev) => {
        if (!prev) return prev;
        return { ...prev, autoEmail: autoEmail ? 1 : 0 };
      });
    } catch (error) {
      console.error('Error updating email preference:', error);
    }
  }, []);

  const updateUserCategories = useCallback(async (categoryIds: string[]) => {
    try {
      const response = await fetch('/api/user/categories', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ categoryIds }),
      });

      if (!response.ok) {
        console.error('Failed to update category preferences');
        return;
      }

      setUser((prev) => {
        if (!prev) return prev;
        return { ...prev, preferredCategories: categoryIds };
      });
    } catch (error) {
      console.error('Error updating category preferences:', error);
    }
  }, []);

  // Use a ref to avoid re-rendering when user changes
  const userRef = useRef<User | null>(null);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // In MVP mode, any logged-in user has unlimited listening.
  const hasActiveSubscription = useCallback(() => {
    if (!user) return false;
    return Boolean(user.email);
  }, [user]);

  // Check if user has exceeded their listening limit
  // Returns the modal type to show, or null if within limits
  const checkListeningLimit = useCallback((): LimitModalType => {
    if (!user) return null;
    return user.email ? null : 'signin';
  }, [user]);

  const logout = () => {
    fetch('/api/logout', {
      credentials: 'include', // Include cookies in request
    })
      .then(() => {
        // Refresh the page to reset auth/user
        window.location.reload();
      })
      .catch((error) => {
        console.error('Error logging out:', error);
      });
  };

  const deleteAccount = async () => {
    try {
      const response = await fetch('/api/account', {
        method: 'DELETE',
        credentials: 'include',
      });
      if (response.ok) {
        window.location.href = '/';
      } else {
        console.error('Failed to delete account');
      }
    } catch (error) {
      console.error('Error deleting account:', error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        loading,
        user,
        setUser,
        refreshUser,
        incrementUserSeconds,
        updateUserSpeed,
        updateAutoEmail,
        updateUserCategories,
        logout,
        deleteAccount,
        hasActiveSubscription,
        checkListeningLimit,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider };
